import { useQuery } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../../apiHelper';

/** Vue d'un dossier Skipcar+ renvoyée par GET /api/skipcar-plus/dossiers/:dealId. */
export type Phase = 'cotation' | 'montage' | 'achat' | 'portage' | 'vente' | 'solde';
export type NatureCondition = 'qonto' | 'manuel' | 'sans_objet';

export interface ConditionVue {
  cle: 'frais_achat' | 'mandat' | 'contrat' | 'caution';
  libelle: string;
  nature: NatureCondition;
  validee: boolean;
  detail: string;
}

export interface EtapeAchatVue {
  cle: string;
  libelle: string;
  etat: 'fait' | 'prochaine' | 'attente';
  resultat: string | null;
}

export interface LigneSimulationVue {
  semaine: number;
  palier: number;
  taux: number;
  montantHtCentimes: number;
  cumulHtCentimes: number;
}

export interface BlocPrelevementVue {
  numero: number;
  premiereSemaine: number;
  derniereSemaine: number;
  datePrelevement: string;
  palier: number;
  montantHtCentimes: number;
  montantTtcCentimes: number;
}

export interface ValidationVue {
  validee: boolean;
  le: string | null;
  par: string | null;
}

export interface DossierSkipcarPlus {
  dealId: number;
  vehicule: { immatriculation: string | null; marque: string | null; modele: string | null };
  agence: { orgId: number | null; nom: string | null };
  statut: 'pending' | 'client_validated' | 'skipcar_validated' | 'rejected';
  phase: Phase;
  montants: {
    couvertureCentimes: number | null;
    prixClientCentimes: number | null;
    prixVenteEstimeCentimes: number | null;
    participationHtCentimes: number;
    rotationEstimeeSemaines: number;
  };
  validations: { client: ValidationVue; skipcar: ValidationVue };
  j0: {
    date: string | null;
    source: 'saisi' | 'da' | 'previsionnel' | null;
    estime: boolean;
    previsionnel: string | null;
    saisi: string | null;
    dateDa: string | null;
  };
  grille: {
    profilId: number | null;
    profilNom: string | null;
    taux: [number, number, number, number];
    derogation: boolean;
    rythme: 'terme_echu' | 'avance';
    /** Faux pour un contrat antérieur à l'outil : taux et rythme réels à reprendre. */
    figee: boolean;
  };
  factureFraisAchat: { numero: string | null; statut: string } | null;
  conditions: ConditionVue[];
  bilanConditions: { validees: number; total: number };
  mandat: { signeLe: string | null; lienSignature: string; fraisRejetHtEuros: number };
  processusAchat: EtapeAchatVue[];
  simulation: LigneSimulationVue[];
  echeancier: BlocPrelevementVue[];
  vueDieu: boolean;
  historique: Array<{ event: string; payload: unknown; author: string | null; created_at: string }>;
}

export interface ProfilTaux {
  id: number;
  nom: string;
  grille: [number, number, number, number];
  systeme: boolean;
}

export interface AgenceSkipcarPlus {
  orgId: number;
  nom: string;
  nombreDossiers: number;
  profilTauxId: number | null;
  profilTauxNom: string | null;
  mandatSigneLe: string | null;
}

/** Connexion OAuth de Skipcar à Qonto (lot 5), renvoyée par GET /api/qonto/oauth/status. */
export interface StatutConnexionQonto {
  configure: boolean;
  connecte: boolean;
  organisation: string | null;
  connecteLe: string | null;
  connectePar: string | null;
  renouveleLe: string | null;
  scopes: string[];
  /** 'ok' si Qonto a répondu avec le jeton, sinon la raison de l'échec. */
  verification: string | null;
}

const BASE = '/api/skipcar-plus';

export const useSkipcarPlusDossier = (dealId: string | undefined) =>
  useQuery({
    queryKey: ['skipcarPlusDossier', dealId],
    queryFn: async (): Promise<DossierSkipcarPlus> => {
      const reponse = await makeAuthenticatedApiCall('get', `${BASE}/dossiers/${dealId}`);
      return reponse.data.data as DossierSkipcarPlus;
    },
    enabled: !!dealId,
    retry: false,
  });

export const useSkipcarPlusProfils = () =>
  useQuery({
    queryKey: ['skipcarPlusProfils'],
    queryFn: async (): Promise<{ profils: ProfilTaux[]; agences: AgenceSkipcarPlus[] }> => {
      const reponse = await makeAuthenticatedApiCall('get', `${BASE}/rate-profiles`);
      return reponse.data.data;
    },
  });

export const useConnexionQonto = () =>
  useQuery({
    queryKey: ['connexionQonto'],
    queryFn: async (): Promise<StatutConnexionQonto> => {
      const reponse = await makeAuthenticatedApiCall('get', '/api/qonto/oauth/status');
      return reponse.data.data as StatutConnexionQonto;
    },
    retry: false,
  });

/** URL de la page d'accord Qonto : le navigateur doit s'y rendre lui-même. */
export const demanderUrlConnexionQonto = async (): Promise<string> => {
  const reponse = await makeAuthenticatedApiCall('get', '/api/qonto/oauth/authorize');
  return reponse.data.data.url as string;
};

export const skipcarPlusActions = {
  setJ0Previsionnel: (dealId: number, date: string | null) =>
    makeAuthenticatedApiCall('put', `${BASE}/dossiers/${dealId}/j0-previsionnel`, { date }),
  setJ0Effectif: (dealId: number, date: string | null) =>
    makeAuthenticatedApiCall('put', `${BASE}/dossiers/${dealId}/j0-effectif`, { date }),
  setCondition: (dealId: number, condition: 'contrat' | 'caution', validee: boolean) =>
    makeAuthenticatedApiCall('put', `${BASE}/dossiers/${dealId}/conditions/${condition}`, { validee }),
  emettreFactureFraisAchat: (dealId: number, montantHtEuros: number) =>
    makeAuthenticatedApiCall('post', `${BASE}/dossiers/${dealId}/purchase-fees-invoice`, { montantHtEuros }),
  synchroniserFactureFraisAchat: (dealId: number) =>
    makeAuthenticatedApiCall('post', `${BASE}/dossiers/${dealId}/purchase-fees-invoice/sync`),
  annulerFactureEnAttente: (dealId: number) =>
    makeAuthenticatedApiCall('put', `${BASE}/dossiers/${dealId}/purchase-fees-invoice/cancel-pending`),
  deroger: (dealId: number, grille: number[]) =>
    makeAuthenticatedApiCall('put', `${BASE}/dossiers/${dealId}/rates`, { grille }),
  appliquerProfil: (dealId: number, profilId: number) =>
    makeAuthenticatedApiCall('put', `${BASE}/dossiers/${dealId}/rates`, { profilId }),
  creerProfil: (nom: string, grille: number[]) => makeAuthenticatedApiCall('post', `${BASE}/rate-profiles`, { nom, grille }),
  modifierProfil: (id: number, grille: number[]) =>
    makeAuthenticatedApiCall('put', `${BASE}/rate-profiles/${id}`, { grille }),
  profilAgence: (orgId: number, profilId: number) =>
    makeAuthenticatedApiCall('put', `${BASE}/agencies/${orgId}/rate-profile`, { profilId }),
  mandatAgence: (orgId: number, signeLe: string | null) =>
    makeAuthenticatedApiCall('put', `${BASE}/agencies/${orgId}/mandate`, { signeLe }),
};

/** Message d'erreur lisible d'un appel API (format { success: false, error }). */
export const messageErreur = (erreur: unknown): string => {
  const reponse = (erreur as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  return reponse?.error ?? reponse?.message ?? "L'opération a échoué";
};
